import React, { createContext, useContext, useEffect, useRef } from 'react';
import ChatLayerSelector from 'components/DisasterGPT/ChatLayerSelector';
import {
  OPORD_ANALYTICS_SECTION_TEMPLATE_ID,
  isAnalyticsSection,
} from 'components/DisasterGPT/constants/opordSectionDefaults';
import { isAnalyticsSelectableLayer } from 'components/DisasterGPT/utils/buildAiLayerCatalog';
import type { SelectedLayerForAI } from 'components/DisasterGPT/utils/prefetchLayerFeaturesForAI';
import {
  useAddSitrepSectionV2,
  useRemoveSitrepSectionV2,
  useUpdateSitrepSectionV2,
  type SitrepSectionType,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import {
  filterLayerStatsDataString,
  isVirtualAnalyticsLayerSection,
  selectedLayersOnSection,
} from './expandAnalyticsLayerSections';

type LayerRef = {
  id?: string;
  kind?: string;
  name?: string;
};

type AnalyticsLayerActionsValue = {
  commitLayers: (
    parent: SitrepSectionType,
    nextLayers: LayerRef[]
  ) => Promise<void>;
};

const AnalyticsLayerActionsContext =
  createContext<AnalyticsLayerActionsValue | null>(null);

/** Survives a Strict Mode remount so a draft is not split twice. */
const analyticsSplitsInFlight = new Set<string>();

export function useAnalyticsLayerActions() {
  return useContext(AnalyticsLayerActionsContext);
}

/** Layer picker on an Analytics category. Each choice is its own child section. */
export function AnalyticsCategoryLayerPicker({
  parent,
  childSections,
}: {
  parent: SitrepSectionType;
  childSections: SitrepSectionType[];
}) {
  const actions = useAnalyticsLayerActions();
  if (!actions || !isAnalyticsSection(parent) || childSections.length === 0) {
    return null;
  }
  if (childSections.some((child) => isVirtualAnalyticsLayerSection(child))) {
    return null;
  }
  const layers = childSections.flatMap((child) =>
    selectedLayersOnSection(child)
  ) as SelectedLayerForAI[];
  return (
    <div className="tw:mb-3 tw:min-w-0">
      <ChatLayerSelector
        selectedLayers={layers}
        onChange={(next) => {
          void actions.commitLayers(parent, next);
        }}
        filterByDatetime={false}
        onFilterByDatetimeChange={() => undefined}
        syncToMap
        isLayerAllowed={isAnalyticsSelectableLayer}
      />
    </div>
  );
}

function layerKey(layer: LayerRef): string {
  return `${layer.kind ?? 'external'}:${layer.id ?? ''}`;
}

function parentLayersAssetExposure(
  section: SitrepSectionType
): Record<string, unknown> | null {
  const sources = (
    Array.isArray(section.data_sources) ? section.data_sources : []
  ) as Array<{ source?: string; assetExposure?: unknown }>;
  const exposure = sources.find(
    (source) => source?.source === 'Layers'
  )?.assetExposure;
  return exposure && typeof exposure === 'object'
    ? (exposure as Record<string, unknown>)
    : null;
}

/**
 * Drafts store Analytics as a category: one child section per selected layer.
 * Choosing a layer creates that child. Removing it deletes the child.
 */
export function AnalyticsLayerActionsProvider({
  sitrep,
  sections,
  enabled,
  children,
}: {
  sitrep: GetSitrepsResponse;
  sections: SitrepSectionType[] | undefined;
  enabled: boolean;
  children: React.ReactNode;
}) {
  const addSection = useAddSitrepSectionV2();
  const removeSection = useRemoveSitrepSectionV2();
  const updateSection = useUpdateSitrepSectionV2();
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const chainRef = useRef(Promise.resolve());

  const commitLayers = (parent: SitrepSectionType, nextLayers: LayerRef[]) => {
    const run = async () => {
      const current = sectionsRef.current ?? [];
      const realChildren = current.filter(
        (section) =>
          section.parent_section_id === parent.id &&
          !isVirtualAnalyticsLayerSection(section) &&
          isAnalyticsSection(section)
      );
      const wanted = nextLayers.filter((layer) => layer.id);
      const wantedKeys = new Set(wanted.map(layerKey));
      const freshParent =
        current.find((section) => section.id === parent.id) ?? parent;
      const parentAssetExposure = parentLayersAssetExposure(freshParent);
      const childByKey = new Map<string, SitrepSectionType>();
      for (const child of realChildren) {
        for (const layer of selectedLayersOnSection(child)) {
          if (layer.id) childByKey.set(layerKey(layer), child);
        }
      }

      for (const layer of wanted) {
        const key = layerKey(layer);
        if (childByKey.has(key)) continue;
        const parentVersion = parent.section_versions?.[0];
        await addSection.mutateAsync({
          sitrepId: sitrep.id,
          sectionTemplateId: OPORD_ANALYTICS_SECTION_TEMPLATE_ID,
          selectedDatetime: new Date().toISOString(),
          sitrep_source: sitrep.sitrep_source,
          sitrep_source_id: sitrep.sitrep_source_id,
          order: (parent.order ?? 0) + realChildren.length + 1,
          aor_id: sitrep.aor_id ?? '',
          parent_section_id: parent.id,
          name: layer.name?.trim() || 'Layer',
          data_sources: [
            {
              source: 'Layers',
              selectedLayers: [layer],
              ...(parentAssetExposure
                ? { assetExposure: parentAssetExposure }
                : {}),
            },
          ],
          skipProcessing: true,
          initialDataString: filterLayerStatsDataString(
            parentVersion?.data_string,
            [layer]
          ),
          initialSectionText: `<p>${layer.name?.trim() || 'Layer'}</p>`,
        });
      }

      for (const [key, child] of childByKey) {
        if (wantedKeys.has(key)) continue;
        await removeSection.mutateAsync({
          sitrepId: sitrep.id,
          sectionId: child.id,
        });
      }

      const parentLayers = selectedLayersOnSection(parent);
      if (parentLayers.length > 0) {
        const sources = Array.isArray(parent.data_sources)
          ? parent.data_sources
          : [];
        const nextSources = sources.map((source) =>
          source?.source === 'Layers'
            ? { ...source, selectedLayers: [] }
            : source
        );
        const cleared =
          nextSources.length > 0
            ? nextSources
            : [{ source: 'Layers', selectedLayers: [] }];
        await updateSection.mutateAsync({
          sitrepId: sitrep.id,
          sectionId: parent.id,
          silent: true,
          updatedFields: {
            data_sources: cleared,
          },
        });
      }
    };

    const next = chainRef.current.then(run, run);
    chainRef.current = next.then(
      () => undefined,
      () => undefined
    );
    return next;
  };

  useEffect(() => {
    if (!enabled || !sections) return;
    for (const section of sections) {
      if (section.parent_section_id || !isAnalyticsSection(section)) continue;
      const layers = selectedLayersOnSection(section).filter(
        (layer) => layer.id
      );
      const hasRealChild = sections.some(
        (child) =>
          child.parent_section_id === section.id &&
          !isVirtualAnalyticsLayerSection(child)
      );
      if (hasRealChild || layers.length === 0) continue;
      const flightKey = `${sitrep.id}:${section.id}`;
      if (analyticsSplitsInFlight.has(flightKey)) continue;
      analyticsSplitsInFlight.add(flightKey);
      void commitLayers(section, layers).catch(() => {
        analyticsSplitsInFlight.delete(flightKey);
      });
    }
    // commitLayers closes over the latest mutations; the sections list is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sections, sitrep.id]);

  if (!enabled) return <>{children}</>;

  return (
    <AnalyticsLayerActionsContext.Provider value={{ commitLayers }}>
      {children}
    </AnalyticsLayerActionsContext.Provider>
  );
}
